import { useEffect, useState, useCallback } from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Container,
  Divider,
  Stack,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { ArrowBack, CloudUpload, Save } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { Vehicle } from "../../../state/types";
import ImageOrderList from "./ImageOrderList";
import { uploadAndProcessImages, updateImageTag } from "./uploadRequests";
import "./upload-dialog.css";

interface UploadDialogProps {
  vehicle: Vehicle;
  onSaveComplete?: (updatedVehicle: Vehicle) => void;
}

export interface MediaFile {
  file: File;
  mediaGroup: string;
}

// Hidden input for file uploads
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * Dialog/page for editing vehicle images and media content
 */
const UploadDialog = ({ vehicle, onSaveComplete }: UploadDialogProps) => {
  // State management
  const [editVehicle, setEditVehicle] = useState<Vehicle>(structuredClone(vehicle));
  const [uploadedFiles, setUploadedFiles] = useState<MediaFile[]>([]);
  const [currentImageType, setCurrentImageType] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info" as "info" | "success" | "error"
  });

  // Track which image types have been reordered for saving later
  const [modifiedImageTypes, setModifiedImageTypes] = useState<Record<string, boolean>>({});

  // Debug log for uploaded files
  useEffect(() => {
    console.log("Uploaded files:", uploadedFiles);
  }, [uploadedFiles]);

  // Debug log for vehicle changes
  useEffect(() => {
    console.log("Vehicle state changed", modifiedImageTypes);
  }, [editVehicle, modifiedImageTypes]);

  // Handle file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length || !currentImageType) return;

    const file = event.target.files[event.target.files.length - 1];

    setUploadedFiles((prevFiles) => [
      ...prevFiles,
      {
        file,
        mediaGroup: currentImageType
      }
    ]);

    // Reset the input value to allow uploading the same file again if needed
    event.target.value = '';
  }, [currentImageType]);

  // Handle vehicle data changes (including image reordering)
  const handleVehicleChange = useCallback((updatedVehicle: Vehicle) => {
    setEditVehicle(updatedVehicle);

    // Compare with original vehicle to detect which image types were modified
    Object.keys(updatedVehicle).forEach(key => {
      if (key.startsWith('image') && Array.isArray(updatedVehicle[key as keyof Vehicle])) {
        // Check if this image array has changed from the original
        if (JSON.stringify(updatedVehicle[key as keyof Vehicle]) !==
          JSON.stringify(vehicle[key as keyof Vehicle])) {
          setModifiedImageTypes(prev => ({ ...prev, [key]: true }));
        }
      }
    });
  }, [vehicle]);

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Handle saving files and order changes
  const handleSave = useCallback(async () => {
    if (uploadedFiles.length === 0 && Object.keys(modifiedImageTypes).length === 0) {
      setNotification({
        open: true,
        message: "No changes to save",
        severity: "info"
      });
      return;
    }

    setIsSaving(true);

    try {
      // Prepare the modified image arrays for saving
      const modifiedImageArrays: Record<string, any[]> = {};

      Object.keys(modifiedImageTypes).forEach(imageType => {
        if (modifiedImageTypes[imageType]) {
          modifiedImageArrays[imageType] = editVehicle[imageType as keyof Vehicle] as any[];
        }
      });

      // Upload new files and update order changes
      const results = await uploadAndProcessImages(
        vehicle,
        uploadedFiles,
        modifiedImageArrays
      );

      console.log("Save results:", results);

      // Show success message
      setNotification({
        open: true,
        message: "Changes saved successfully",
        severity: "success"
      });

      // Reset state
      setUploadedFiles([]);
      setModifiedImageTypes({});

      // Notify parent component if provided
      if (onSaveComplete) {
        onSaveComplete(editVehicle);
      }
    } catch (error) {
      console.error("Error saving changes:", error);

      setNotification({
        open: true,
        message: "Error saving changes. Please try again.",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  }, [uploadedFiles, editVehicle, vehicle, modifiedImageTypes, onSaveComplete]);

  // Disable upload button if no image type is selected
  const isUploadDisabled = !currentImageType;

  // Check if there are any changes to save
  const hasChanges = uploadedFiles.length > 0 || Object.keys(modifiedImageTypes).length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              // Get current URL and remove "/edit" from the end
              const currentUrl = window.location.href;
              const newUrl = currentUrl.replace(/\/edit$/, '');
              window.location.href = newUrl;
            }}
            sx={{ mr: 2, color: 'white' }}
          >
            Back
          </Button>
          <Typography variant="h5" component="h1" gutterBottom>
            Vehicle Media Manager
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {editVehicle.assetIdentifier}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Button
            component="label"
            role={undefined}
            variant="contained"
            disabled={isUploadDisabled}
            startIcon={<CloudUpload />}
            color="primary"
            size="large"
            sx={{
              mb: 3,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            Upload {currentImageType ? currentImageType.replace('image', '') : 'Files'}
            <VisuallyHiddenInput
              type="file"
              onChange={handleFileUpload}
              accept="image/*"
            />
          </Button>

          {isUploadDisabled && (
            <Typography variant="caption" color="text.secondary">
              Please select an image type first
            </Typography>
          )}
        </Stack>

        <Box sx={{ mb: 4 }}>
          <ImageOrderList
            vehicle={editVehicle}
            onChange={handleVehicleChange}
            uploadedFiles={uploadedFiles}
            setImageType={setCurrentImageType}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      {/* Notification toast */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UploadDialog;